import { useState, useEffect } from "react";
import {
  Circle,
  ExternalLink,
  Lightbulb,
  Bookmark,
  StickyNote,
  Save,
} from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import LlcNameChecker from "../LlcNameChecker";
import ClippedYouTube from "./ClippedYouTube";
import MilestoneShare from "./MilestoneShare";

export default function ChapterView({
  chapter,
  ChapterIcon,
  completedSteps,
  checklistStart,
  onToggle,
  isBookmarked,
  onToggleBookmark,
  noteText,
  onNoteSave,
  referralCode,
}) {
  const [localNote, setLocalNote] = useState(noteText);
  const [noteOpen, setNoteOpen] = useState(!!noteText);

  // Sync when chapter changes
  useEffect(() => {
    setLocalNote(noteText);
    setNoteOpen(!!noteText);
  }, [noteText, chapter.id]);

  return (
    <div data-testid={`chapter-${chapter.id}`} className="space-y-8">
      {/* Header */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[var(--c-border)] bg-[#1E3A8A] shadow-[3px_3px_0px_var(--c-shadow)]">
              <ChapterIcon className="h-6 w-6 text-white" />
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--c-muted)]">
              Step {chapter.id} of 9
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid={`toggle-notes-${chapter.id}`}
              onClick={() => setNoteOpen(!noteOpen)}
              className={`flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-bold transition-all hover:-translate-y-0.5 ${
                noteText
                  ? "border-[#A3E6D5] bg-[#A3E6D5]/15 text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)]"
                  : "border-[var(--c-border-medium)] bg-[var(--c-surface)] text-[var(--c-muted)] hover:border-[var(--c-border)] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
              }`}
            >
              <StickyNote className="h-3.5 w-3.5" />
              Notes
            </button>
            <button
              data-testid={`bookmark-chapter-${chapter.id}`}
              onClick={onToggleBookmark}
              className={`flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-bold transition-all hover:-translate-y-0.5 ${
                isBookmarked
                  ? "border-[#1E3A8A] bg-[#1E3A8A]/10 text-[#1E3A8A] shadow-[2px_2px_0px_var(--c-shadow)]"
                  : "border-[var(--c-border-medium)] bg-[var(--c-surface)] text-[var(--c-muted)] hover:border-[var(--c-border)] hover:shadow-[2px_2px_0px_var(--c-shadow)]"
              }`}
            >
              <Bookmark className={`h-3.5 w-3.5 ${isBookmarked ? "fill-[#1E3A8A]" : ""}`} />
              {isBookmarked ? "Saved" : "Save"}
            </button>
          </div>
        </div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[var(--c-text)] sm:text-3xl lg:text-4xl">
          {chapter.title}
        </h2>
        <p className="mt-2 text-base text-[var(--c-muted)] sm:text-lg">{chapter.subtitle}</p>
      </div>

      {/* Notes Panel */}
      {noteOpen && (
        <div
          data-testid={`notes-panel-${chapter.id}`}
          className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-checklist-bg)] p-5 shadow-[3px_3px_0px_var(--c-shadow)]"
        >
          <div className="mb-3 flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-[#1E3A8A]" />
            <h4 className="font-heading text-sm font-bold text-[var(--c-text)]">Your Notes</h4>
          </div>
          <textarea
            data-testid={`notes-textarea-${chapter.id}`}
            value={localNote}
            onChange={(e) => setLocalNote(e.target.value)}
            placeholder="Add your personal notes, reminders, or key takeaways for this chapter..."
            rows={4}
            className="w-full resize-none rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-surface)] p-3 text-sm text-[var(--c-text)] placeholder:text-[var(--c-muted)] focus:border-[#1E3A8A] focus:outline-none focus:ring-1 focus:ring-[#1E3A8A]/20"
          />
          <div className="mt-2 flex justify-end">
            <button
              data-testid={`save-note-${chapter.id}`}
              onClick={() => onNoteSave(localNote)}
              className="flex items-center gap-1.5 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-4 py-1.5 text-xs font-bold text-white shadow-[2px_2px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_var(--c-shadow)]"
            >
              <Save className="h-3 w-3" />
              Save Note
            </button>
          </div>
        </div>
      )}

      {/* Intro */}
      <p className="text-base leading-relaxed text-[var(--c-text)]">{chapter.content.intro}</p>

      {/* Video Walkthrough - Chapter 1 */}
      {chapter.id === 1 && (
        <>
          <div data-testid="chapter-video-1" className="overflow-hidden rounded-xl border-2 border-[var(--c-border)] shadow-[3px_3px_0px_var(--c-shadow)]">
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <ClippedYouTube
                videoId="x-53LljHvXU"
                endSeconds={198}
                title="Step 1: Choose a Unique LLC Name"
              />
            </div>
          </div>
          <div
            data-testid="video-note-1"
            className="rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-checklist-bg)] p-4 text-sm leading-relaxed text-[var(--c-text)]"
          >
            <span className="font-bold">Note:</span> Go to the Secretary of State website and click on Name Availability Search.
          </div>
        </>
      )}

      {/* Sections */}
      {chapter.content.sections.map((section) => (
        <div key={`${chapter.id}-section-${section.heading}`}>
          <h3 className="font-heading text-xl font-semibold text-[var(--c-text)] sm:text-2xl">{section.heading}</h3>
          <ul className="mt-3 space-y-2">
            {section.items.map((item) => (
              <li key={`${chapter.id}-${section.heading}-${item}`} className="flex items-start gap-3">
                <Circle className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 fill-[#1E3A8A] text-[#1E3A8A]" />
                <span className="text-[var(--c-text)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* LLC Name Checker Tool - only for Chapter 1 */}
      {chapter.id === 1 && <LlcNameChecker />}

      {/* Pro Tip */}
      <div className="rounded-xl border-2 border-[var(--c-border)] bg-[#A3E6D5]/15 p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
        <div className="flex items-start gap-3">
          <Lightbulb className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#1E3A8A]" />
          <div>
            <p className="font-heading text-sm font-bold uppercase tracking-wider text-[var(--c-text)]">Pro Tip</p>
            <p className="mt-1 text-sm leading-relaxed text-[var(--c-text)]">{chapter.content.proTip}</p>
          </div>
        </div>
      </div>

      {/* Video Walkthrough - Chapter 3 (starts at 3:18) */}
      {chapter.id === 3 && (
        <div data-testid="chapter-video-3" className="overflow-hidden rounded-xl border-2 border-[var(--c-border)] shadow-[3px_3px_0px_var(--c-shadow)]">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              className="absolute inset-0 h-full w-full"
              src="https://www.youtube.com/embed/x-53LljHvXU?start=198"
              title="Step 3: File Certificate of Formation"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* External Links */}
      {chapter.links.length > 0 && (
        <div>
          <h3 className="font-heading text-lg font-semibold text-[var(--c-text)]">Helpful Links</h3>
          <div className="mt-3 space-y-2">
            {chapter.links.map((link) => (
              <a
                key={`${chapter.id}-link-${link.url}`}
                data-testid={`external-link-${chapter.id}-${link.url}`}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-3 font-medium text-[var(--c-text)] transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_var(--c-shadow)]"
              >
                <ExternalLink className="h-4 w-4 flex-shrink-0 text-[#1E3A8A]" />
                <span className="text-sm">{link.label}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Checklist */}
      <div>
        <h3 className="font-heading text-lg font-semibold text-[var(--c-text)]">Your Checklist</h3>
        <div className="mt-3 space-y-3">
          {chapter.checklistItems.map((item, ci) => {
            const idx = checklistStart + ci;
            const checked = completedSteps.includes(idx);
            return (
              <label
                key={`${chapter.id}-item-${ci}`}
                data-testid={`checklist-item-${chapter.id}-${ci}`}
                className="flex cursor-pointer items-start gap-3 rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-checklist-bg)] p-4 transition-all hover:border-[var(--c-border-medium)]"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggle(idx)}
                  className="mt-0.5 h-6 w-6 rounded-md border-2 border-[var(--c-border)] data-[state=checked]:bg-[#A3E6D5] data-[state=checked]:text-[#1A1918]"
                />
                <span className={`text-sm font-medium transition-all ${checked ? "text-[var(--c-muted)] line-through" : "text-[var(--c-text)]"}`}>
                  {item}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Milestone share — visible once all checklist items in this chapter are complete */}
      {chapter.checklistItems.every((_, ci) => completedSteps.includes(checklistStart + ci)) && (
        <MilestoneShare
          chapterId={chapter.id}
          chapterTitle={chapter.title}
          referralCode={referralCode}
        />
      )}
    </div>
  );
}
